import { Close } from "@mui/icons-material";
import { Box, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import { COLORS, disabledContainedButtonSx } from "assets";
import useTranslation from "hooks/useTranslation";
import React from "react";
import { IModal } from "types";

const darkModalFormSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: COLORS.surface.card,
    color: COLORS.surface.primaryText,
    borderRadius: `${COLORS.radius.sm}px`,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS.surface.border
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS.surface.secondaryText
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS.primary
    },
    "& textarea, & input": {
      color: COLORS.surface.primaryText
    }
  },
  "& .MuiInputLabel-root": {
    color: COLORS.surface.secondaryText,
    "&.Mui-focused": { color: COLORS.primary },
    "&.MuiInputLabel-shrink": {
      bgcolor: COLORS.surface.card,
      px: 0.5
    }
  },
  "& [class*='StyledInputRoot']": {
    bgcolor: `${COLORS.surface.card} !important`,
    color: COLORS.surface.primaryText,
    border: `1px solid ${COLORS.surface.border}`,
    borderRadius: `${COLORS.radius.sm}px`
  },
  "& [class*='StyledInputElement']": {
    color: `${COLORS.surface.primaryText} !important`,
    WebkitTextFillColor: `${COLORS.surface.primaryText} !important`
  },
  "& [class*='StyledInputLabel']": {
    color: `${COLORS.surface.secondaryText} !important`
  },
  "& .MuiButton-containedPrimary": {
    color: `${COLORS.white} !important`,
    backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
    boxShadow: "none",
    "&:hover": {
      backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
      filter: "brightness(1.05)",
      boxShadow: "none"
    },
    "&.Mui-disabled": disabledContainedButtonSx,
    "& .MuiCircularProgress-root": {
      color: COLORS.white
    }
  },
  "& .MuiButton-containedError": {
    color: `${COLORS.white} !important`,
    "& .MuiCircularProgress-root": {
      color: COLORS.white
    }
  },
  "& .MuiFormControlLabel-label": {
    color: "rgba(255,255,255,0.9)"
  },
  "& .MuiCheckbox-root": {
    color: "rgba(255,255,255,0.7)"
  }
};

const lightModalFormSx = {
  "& [class*='StyledInputLabel']": {
    color: `${COLORS.surface.secondaryText} !important`,
    fontWeight: "500 !important",
    fontSize: "0.8125rem !important",
    marginBottom: "6px !important"
  },
  "& .MuiButton-outlined": {
    borderColor: COLORS.surface.border,
    color: COLORS.surface.primaryText,
    borderRadius: `${COLORS.radius.md}px`,
    textTransform: "none",
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      borderColor: COLORS.surface.secondaryText,
      bgcolor: COLORS.surface.tableHeader
    }
  },
  "& .MuiButton-containedPrimary": {
    color: `${COLORS.white} !important`,
    backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
    borderRadius: `${COLORS.radius.md}px`,
    textTransform: "none",
    fontWeight: 500,
    boxShadow: "none",
    "&:hover": {
      backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
      filter: "brightness(1.05)",
      boxShadow: "none"
    },
    "&.Mui-disabled": disabledContainedButtonSx,
    "& .MuiCircularProgress-root": {
      color: COLORS.white
    }
  }
};

export const MyModal: React.FC<IModal> = ({ children, title, subtitle, open, setOpen, variant = "dark", width }) => {
  const onClose = () => setOpen(false);
  const t = useTranslation();
  const isLight = variant === "light";
  const modalWidth = width ?? (isLight ? 520 : 700);
  const contentPx = isLight ? 3.5 : 3;
  const cornerInset = COLORS.radius.lg; // keep track clear of rounded corners

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2
      }}
    >
      <Box
        sx={{
          position: "relative",
          outline: "none",
          boxShadow: isLight ? COLORS.shadow.card : COLORS.shadow.modal,
          maxWidth: "98%",
          borderRadius: `${COLORS.radius.lg}px`,
          width: modalWidth,
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // clips content + scrollbar to rounded corners
          ...(isLight
            ? {
                bgcolor: COLORS.surface.card,
                border: `1px solid ${COLORS.surface.border}`
              }
            : {
                backgroundImage: `linear-gradient(${COLORS.gradients.background.top}, ${COLORS.gradients.background.bottom})`,
                backgroundAttachment: "fixed",
                border: "1px solid rgba(255,255,255,0.12)"
              })
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "auto",
            scrollbarColor: isLight
              ? `rgba(98, 108, 122, 0.65) ${COLORS.surface.track}`
              : "rgba(255,255,255,0.5) rgba(255,255,255,0.1)",
            "&::-webkit-scrollbar": {
              width: 14
            },
            "&::-webkit-scrollbar-button": {
              display: "none",
              width: 0,
              height: 0
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: isLight ? COLORS.surface.track : "rgba(255,255,255,0.1)",
              borderLeft: isLight ? `1px solid ${COLORS.surface.border}` : "1px solid rgba(255,255,255,0.14)",
              // Pull track off the rounded top/bottom corners
              marginBlock: `${cornerInset}px`,
              borderRadius: 8
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isLight ? "rgba(98, 108, 122, 0.65)" : "rgba(255, 255, 255, 0.5)",
              borderRadius: 8,
              border: isLight ? `3px solid ${COLORS.surface.track}` : "3px solid rgba(14, 12, 20, 0.25)",
              backgroundClip: "padding-box",
              minHeight: 48
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: isLight ? "rgba(98, 108, 122, 0.85)" : "rgba(255, 255, 255, 0.65)"
            },
            "&::-webkit-scrollbar-corner": {
              background: "transparent"
            },
            ...(isLight ? lightModalFormSx : darkModalFormSx)
          }}
        >
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            sx={{
              px: contentPx,
              pt: isLight ? 3.5 : 3,
              pb: isLight ? 3 : 2
            }}
          >
            <Box minWidth={0} overflow="hidden">
              <Typography
                variant="h3"
                sx={{
                  color: isLight ? COLORS.surface.primaryText : COLORS.white,
                  fontSize: isLight ? 22 : undefined,
                  fontWeight: 600
                }}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: isLight ? COLORS.surface.secondaryText : "rgba(255,255,255,0.7)",
                    mt: 0.5,
                    fontSize: 14,
                    lineHeight: 1.5
                  }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            <Tooltip title={t["Close"]}>
              <IconButton
                onClick={onClose}
                sx={{
                  color: isLight ? COLORS.surface.secondaryText : COLORS.white,
                  border: isLight ? `1px solid ${COLORS.surface.border}` : "none",
                  width: 36,
                  height: 36,
                  flexShrink: 0
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ px: contentPx, pb: isLight ? 3.5 : 3 }}>{children}</Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MyModal;
